/**
 * @fileoverview Department code-set vendor setup and tenant hydration management tab.
 * @module system-web/department-code-sets-tab
 *
 * Responsibilities:
 * - Render vendor credential status and catalog browsing for department code sets.
 * - Manage tenant hydrated code-set rows and rehydrate actions.
 *
 * Dependencies:
 * - `@firesquirrel/local-db` for system Dexie reads and background job live updates.
 * - `codeSetVendorService` for local-first regulatory framework operations and online enqueue actions.
 *
 * Architecture:
 * - Dexie-backed system-web UI; status updates arrive through change stream sync and job watchers.
 * - Online actions are limited to vendor validation/catalog loading and hydrate/rehydrate enqueue operations.
 *
 * Notes:
 * - System cache lifecycle is not managed from this tab; cache is evaluated by the worker during hydration.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Link2, CheckCircle, XCircle, AlertCircle, RefreshCw, Plus, Edit2, Trash2,
  ChevronDown, ChevronRight, Download, BookOpen, Eye, EyeOff, Loader2,
} from 'lucide-react';
import * as codeSetVendorService from '../services/codeSetVendorService';
import type {
  VendorEntry, RegulatoryFramework, CatalogBook, VendorBook,
} from '../services/codeSetVendorService';
import { getSystemDb, getSystemDexieRaw, watchBackgroundJobById, type BackgroundJob } from '@firesquirrel/local-db';
import type { GridTheme } from '@ui-admin/contexts/ThemeContext';
import ConfirmActionModal from '../components/ui/ConfirmActionModal';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type VendorId = 'icc' | 'nfpa' | 'custom';

interface Props {
  tenant: any;
  theme: GridTheme;
  onSave: (fieldKey: string, value: unknown) => Promise<void>;
}

/**
 * @process ResolveCatalogEditionYear
 *
 * Steps:
 * 1. Extract the first four-digit year from a vendor edition string.
 * 2. Return zero when no sortable year is present.
 *
 * Inputs:
 * - edition: Vendor edition or printing label.
 *
 * Outputs:
 * - Numeric year used for newest-first catalog ordering.
 *
 * Failure Handling:
 * - Non-year values safely sort after year-based editions.
 *
 * Idempotency:
 * - REQUIRED
 */
function resolveCatalogEditionYear(edition: string | undefined): number {
  const match = String(edition ?? '').match(/\b(19|20)\d{2}\b/);
  return match ? Number(match[0]) : 0;
}

/**
 * @process ResolveVendorBookIdentity
 *
 * Steps:
 * 1. Combine vendor book id and edition into a stable UI key.
 * 2. Compare assigned and catalog rows by the same identity.
 *
 * Inputs:
 * - Vendor or catalog book identity fields.
 *
 * Outputs:
 * - Stable identity key or equality result.
 *
 * Failure Handling:
 * - Missing editions normalize to an empty string.
 *
 * Idempotency:
 * - REQUIRED
 */
function getVendorBookKey(book: Pick<VendorBook | CatalogBook, 'bookId' | 'edition'>): string {
  return `${book.bookId}::${String(book.edition ?? '')}`;
}

function isSameVendorBook(
  left: Pick<VendorBook | CatalogBook, 'bookId' | 'edition'>,
  right: Pick<VendorBook | CatalogBook, 'bookId' | 'edition'>,
): boolean {
  return getVendorBookKey(left) === getVendorBookKey(right);
}

// ---------------------------------------------------------------------------
// Validation badge
// ---------------------------------------------------------------------------

function ValidationBadge({ status }: { status?: string }) {
  if (status === 'valid') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
      <CheckCircle className="h-3 w-3" /> Valid
    </span>
  );
  if (status === 'invalid') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
      <XCircle className="h-3 w-3" /> Invalid
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
      <AlertCircle className="h-3 w-3" /> Untested
    </span>
  );
}

// ---------------------------------------------------------------------------
// Download status badge
// ---------------------------------------------------------------------------

function DownloadBadge({
  status,
  chapters,
  violations,
  importedAt,
}: {
  status?: string;
  chapters?: number;
  violations?: number;
  importedAt?: string;
}) {
  if (status === 'completed') {
    const importedLabel = importedAt
      ? `Hydrated on ${new Date(importedAt).toLocaleDateString()}`
      : 'Hydrated';
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-green-700"
        title={violations != null ? `${violations.toLocaleString()} violations` : undefined}
      >
        <CheckCircle className="h-3.5 w-3.5" />
        <span>{importedLabel}</span>
      </span>
    );
  }
  if (status === 'downloading' || status === 'pending') return (
    <span className="inline-flex items-center gap-1 text-xs text-blue-600">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      <span>{chapters ? `${chapters} ch` : 'Hydrating…'}</span>
    </span>
  );
  if (status === 'failed') return (
    <span className="inline-flex items-center gap-1 text-xs text-red-600">
      <XCircle className="h-3.5 w-3.5" /> Failed
    </span>
  );
  return null;
}

// ---------------------------------------------------------------------------
// AddVendorModal
// ---------------------------------------------------------------------------

interface AddVendorModalProps {
  existing?: VendorEntry;
  onClose: () => void;
  onSaved: (vendor: VendorEntry) => void;
  tenantId: string;
  theme: GridTheme;
}

function AddVendorModal({ existing, onClose, onSaved, tenantId }: AddVendorModalProps) {
  const [vendorId, setVendorId] = useState<VendorId>(existing?.vendorId ?? 'icc');
  const [clientId, setClientId] = useState(existing?.credentials?.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [subscriptionId, setSubscriptionId] = useState(existing?.credentials?.subscriptionId ?? '');
  const [customName, setCustomName] = useState(existing?.vendorId === 'custom' ? existing.vendorName : '');
  const [showSecret, setShowSecret] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ valid: boolean; message?: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const vendorName = vendorId === 'icc' ? 'ICC Digital Codes'
    : vendorId === 'nfpa' ? 'NFPA LiNK'
    : (customName || 'Custom Vendor');

  async function handleTest() {
    if (!existing && !clientId && !clientSecret && !apiKey) return;
    setTesting(true);
    setTestResult(null);
    try {
      const payload: any = { vendorId, vendorName, credentials: { clientId, subscriptionId } };
      if (vendorId === 'icc') { payload.credentials.clientSecret = clientSecret; }
      if (vendorId === 'nfpa') { payload.credentials.apiKey = apiKey; }
      const result = await codeSetVendorService.validateVendor(tenantId, vendorId, payload);
      setTestResult({ valid: result.valid, message: result.message });
    } catch (e) {
      setTestResult({ valid: false, message: (e as Error).message });
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const payload: any = {
        vendorId,
        vendorName,
        isEnabled: true,
        credentials: { clientId, subscriptionId },
      };
      if (vendorId === 'icc' && clientSecret) payload.credentials.clientSecret = clientSecret;
      if (vendorId === 'nfpa' && apiKey) payload.credentials.apiKey = apiKey;
      const saved = await codeSetVendorService.upsertVendor(tenantId, payload);
      onSaved(saved);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">{existing ? 'Edit Vendor' : 'Add Code Set Vendor'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <p className="text-xs font-medium text-gray-700 mb-2">Select vendor</p>
            <div className="space-y-1.5">
              {(['icc', 'nfpa', 'custom'] as VendorId[]).map(v => (
                <label key={v} className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${vendorId === v ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <input type="radio" name="vendorId" value={v} checked={vendorId === v} onChange={() => { setVendorId(v); setTestResult(null); }} className="accent-orange-500" />
                  <span className="text-sm font-medium text-gray-800">
                    {v === 'icc' ? 'ICC Digital Codes' : v === 'nfpa' ? 'NFPA LiNK' : 'Custom Vendor'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {vendorId === 'custom' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Vendor Name *</label>
              <input type="text" value={customName} onChange={e => setCustomName(e.target.value)}
                placeholder="e.g. State Fire Marshal Codes"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
          )}

          {vendorId === 'icc' && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">ICC Credentials</p>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Client ID *</label>
                <input type="text" value={clientId} onChange={e => setClientId(e.target.value)}
                  placeholder="e.g. icc-client-abc123"
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Client Secret {existing?.credentials?.clientSecret ? '(leave blank to keep existing)' : '*'}
                </label>
                <div className="relative">
                  <input type={showSecret ? 'text' : 'password'} value={clientSecret} onChange={e => setClientSecret(e.target.value)}
                    placeholder={existing?.credentials?.clientSecret ? '••••••••••••••••' : 'Enter client secret'}
                    className="w-full px-3 py-1.5 pr-8 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
                  <button type="button" onClick={() => setShowSecret(s => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Encrypted server-side. Never stored in the browser.</p>
                <p className="text-xs text-gray-500 mt-1">
                  Validation exchanges this tenant&apos;s current plaintext ICC secret for a bearer token at ICC.
                </p>
              </div>
            </div>
          )}

          {vendorId === 'nfpa' && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">NFPA LiNK Credentials</p>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  API Key {existing?.credentials?.apiKey ? '(leave blank to keep existing)' : '*'}
                </label>
                <div className="relative">
                  <input type={showApiKey ? 'text' : 'password'} value={apiKey} onChange={e => setApiKey(e.target.value)}
                    placeholder={existing?.credentials?.apiKey ? '••••••••••••••••' : 'Enter API key'}
                    className="w-full px-3 py-1.5 pr-8 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
                  <button type="button" onClick={() => setShowApiKey(s => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Subscription ID</label>
                <input type="text" value={subscriptionId} onChange={e => setSubscriptionId(e.target.value)}
                  placeholder="e.g. NFPA-2024-PRO"
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">NFPA LiNK integration is not yet available. Credentials will be saved for future use.</p>
              </div>
            </div>
          )}

          {testResult && (
            <div className={`flex items-start gap-2 p-2.5 rounded-lg border text-xs ${testResult.valid ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
              {testResult.valid ? <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" /> : <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />}
              <span>{testResult.valid ? 'Connection successful' : (testResult.message ?? 'Connection failed')}</span>
            </div>
          )}

          {error && (
            <p className="text-xs text-red-600">{error}</p>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
          <button onClick={handleTest} disabled={testing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Test Connection
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Save Vendor
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DownloadProgressRow — polls job status while running
// ---------------------------------------------------------------------------

interface DownloadProgressRowProps {
  tenantId: string;
  book: VendorBook;
  catalogBook: CatalogBook;
  onComplete: (updatedBook: VendorBook) => void;
}

/**
 * @component DownloadProgressRow
 *
 * Purpose:
 * - Render real-time progress for a single in-flight code-set download.
 *
 * Props:
 * - tenantId: Department id; unused after the Dexie migration but kept for
 *   API compatibility with callers that still pass it.
 * - book: Current selectedBooks entry (carries the downloadJobId).
 * - onComplete: Callback invoked once when the job transitions to terminal.
 *
 * Behavior:
 * - Subscribes to the shared `background_jobs` Dexie table via
 *   `watchBackgroundJobById` (Step C gap-closure 2026-05-01). This replaces
 *   the 4-second polling loop that previously hit `/code-set-download/:jobId`.
 * - On terminal status transitions, fires the completion callback ONCE with
 *   the projected VendorBook shape.
 *
 * Data Source:
 * - local (Dexie `background_jobs`, synced from system Mongo change-stream).
 *
 * Constraints:
 * - Must not call the REST API directly for status polling.
 *
 * Performance:
 * - liveQuery only re-emits when the matching row changes, so idle renders
 *   are cheap.
 */
function DownloadProgressRow({ book, onComplete }: DownloadProgressRowProps) {
  const [bgJob, setBgJob] = useState<BackgroundJob | null>(null);
  const [feedState, setFeedState] = useState<'opening' | 'waiting' | 'connected' | 'error'>('opening');
  const completedRef = useRef(false);

  useEffect(() => {
    completedRef.current = false;
    setBgJob(null);
    setFeedState('opening');
    const jobId = book.downloadJobId;
    if (!jobId) return;
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    void (async () => {
      try {
        // Make sure the Dexie instance is open before asking for the raw handle.
        await getSystemDb();
        if (cancelled) return;
        const raw = getSystemDexieRaw();
        if (!raw) {
          setFeedState('waiting');
          return;
        }
        const subscription = watchBackgroundJobById(raw, jobId).subscribe({
          next: (job) => {
            if (cancelled) return;
            setBgJob(job);
            setFeedState(job ? 'connected' : 'waiting');
            if (!job) return;
            if ((job.status === 'completed' || job.status === 'failed') && !completedRef.current) {
              completedRef.current = true;
              const payload = (job.payload ?? {}) as Record<string, any>;
              onComplete({
                ...book,
                downloadStatus: job.status,
                downloadPhase: job.progress?.phase ?? job.status,
                progressPercent: job.progress?.percent ?? book.progressPercent,
                processedRecords: job.progress?.current ?? book.processedRecords,
                chapterCount: typeof payload.chapterCount === 'number' ? payload.chapterCount : book.chapterCount,
                violationCount: typeof payload.violationCount === 'number' ? payload.violationCount : book.violationCount,
                downloadJobId: job._id,
                rawArtifactId: typeof payload.rawArtifactId === 'string' ? payload.rawArtifactId : book.rawArtifactId,
                codeSetId: typeof payload.codeSetId === 'string' ? payload.codeSetId : book.codeSetId,
                cacheHit: typeof payload.cacheHit === 'boolean' ? payload.cacheHit : book.cacheHit,
                hydrationOperation: payload.hydrationOperation === 'rehydrate'
                  ? 'rehydrate'
                  : payload.hydrationOperation === 'hydrate'
                    ? 'hydrate'
                    : book.hydrationOperation,
                downloadedAt: job.completedAt ?? job.updatedAt,
                errorMessage: job.status === 'failed' ? (job.error ?? job.message) : undefined,
              });
            }
          },
          error: (err) => {
            console.warn('[DepartmentCodeSetsTab] watchBackgroundJobById error:', err);
            if (!cancelled) setFeedState('error');
          },
        });
        unsubscribe = () => subscription.unsubscribe();
      } catch (err) {
        console.warn('[DepartmentCodeSetsTab] getSystemDb failed:', err);
        if (!cancelled) setFeedState('error');
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [book.downloadJobId]);

  const processed = bgJob?.progress?.current ?? book.processedRecords ?? 0;
  const pct = Math.max(0, Math.min(100, Math.round(bgJob?.progress?.percent ?? book.progressPercent ?? 0)));
  const phase = bgJob?.message
    ?? bgJob?.progress?.phase
    ?? book.downloadPhase
    ?? (feedState === 'waiting' ? 'Queued — waiting for background operation sync' : undefined)
    ?? (feedState === 'error' ? 'Queued — background operation feed unavailable' : undefined)
    ?? 'Queued for import';
  const payload = (bgJob?.payload ?? {}) as Record<string, any>;
  const chapterCount = typeof payload.chapterCount === 'number' ? payload.chapterCount : book.chapterCount ?? 0;
  const violationCount = typeof payload.violationCount === 'number' ? payload.violationCount : book.violationCount ?? 0;
  const syncDetail = bgJob
    ? (bgJob.status === 'pending' ? 'Safe to leave this page; the job is queued server-side.' : 'Safe to leave this page; progress is tracked server-side.')
    : book.downloadJobId
      ? `Job ${book.downloadJobId} is running; waiting for the synced background job row.`
      : 'Waiting for job id.';

  return (
    <div className="flex items-center gap-2">
      <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between text-xs text-blue-600 mb-0.5">
          <span className="truncate">{phase}</span>
          <span>{pct}%</span>
        </div>
        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-gray-400 mt-0.5">
          {chapterCount} chapters · {violationCount.toLocaleString()} violations · {processed.toLocaleString()} records
        </p>
        <p className="text-[10px] text-gray-400 mt-0.5 truncate" title={syncDetail}>
          {syncDetail}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
/**
 * @component DepartmentCodeSetsTab
 *
 * Purpose:
 * - Manages department vendor setup and tenant hydrated code-set rows.
 *
 * Props:
 * - tenant: System tenant document projection.
 * - theme: Grid shell styling contract.
 * - onSave: Parent persistence callback for regulatory framework updates.
 *
 * Behavior:
 * - Reads tenant regulatory framework from system Dexie and watches background job updates.
 * - Enqueues hydrate/rehydrate operations while preserving one row per assigned department book.
 *
 * Data Source:
 * - hybrid
 *
 * Constraints:
 * - Does not manage system cache rows directly; worker cache due diligence runs for every hydrate.
 *
 * Performance:
 * - Uses memoized callbacks and targeted job watchers to avoid polling.
 */
export function DepartmentCodeSetsTab({ tenant, theme, onSave }: Props) {
  const tenantId = tenant._id ?? tenant.id;

  const [rf, setRf] = useState<RegulatoryFramework>({});
  const [loading, setLoading] = useState(true);

  const [showAddVendor, setShowAddVendor] = useState(false);
  const [editingVendor, setEditingVendor] = useState<VendorEntry | undefined>();
  const [validatingVendorId, setValidatingVendorId] = useState<string | null>(null);

  const [selectedVendorId, setSelectedVendorId] = useState<string>('');
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalog, setCatalog] = useState<CatalogBook[]>([]);
  const [catalogNotice, setCatalogNotice] = useState<string | null>(null);
  const [expandedBookKey, setExpandedBookKey] = useState<string | null>(null);
  const [downloadingBookKey, setDownloadingBookKey] = useState<string | null>(null);
  const [rehydratingBookKey, setRehydratingBookKey] = useState<string | null>(null);
  const [removingBookKey, setRemovingBookKey] = useState<string | null>(null);
  const [removeBookTarget, setRemoveBookTarget] = useState<VendorBook | null>(null);

  const loadRegulatoryFramework = useCallback(async () => {
    setLoading(true);
    try {
      const data = await codeSetVendorService.getRegulatoryFramework(tenantId);
      setRf(data);
      if ((data.codeSetVendors ?? []).length > 0 && !selectedVendorId) {
        setSelectedVendorId(data.codeSetVendors![0].vendorId);
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    setLoading(true);
    const subscription = codeSetVendorService.watchRegulatoryFramework(tenantId).subscribe({
      next: (data) => {
        setRf(data);
        setSelectedVendorId((current) => {
          if (current) return current;
          return data.codeSetVendors?.[0]?.vendorId ?? '';
        });
        setLoading(false);
      },
      error: (error) => {
        console.warn('[DepartmentCodeSetsTab] watchRegulatoryFramework error:', error);
        setLoading(false);
      },
    });

    return () => subscription.unsubscribe();
  }, [tenantId]);

  async function handleValidateVendor(vendorId: string) {
    setValidatingVendorId(vendorId);
    try {
      const currentVendor = rf.codeSetVendors?.find((vendor) => vendor.vendorId === vendorId);
      const payload: Parameters<typeof codeSetVendorService.validateVendor>[2] = currentVendor ? {
        vendorId: currentVendor.vendorId,
        vendorName: currentVendor.vendorName,
        credentials: {
          clientId: currentVendor.credentials?.clientId,
          clientSecret: currentVendor.credentials?.clientSecret,
          apiKey: currentVendor.credentials?.apiKey,
          subscriptionId: currentVendor.credentials?.subscriptionId,
        },
      } : undefined;
      await codeSetVendorService.validateVendor(tenantId, vendorId, payload);
      await loadRegulatoryFramework();
    } finally {
      setValidatingVendorId(null);
    }
  }

  async function handleLoadCatalog() {
    if (!selectedVendorId) return;
    setCatalogLoading(true);
    setCatalog([]);
    setCatalogNotice(null);
    try {
      const result = await codeSetVendorService.getCatalog(tenantId, selectedVendorId);
      // Newest at top, oldest at bottom. Falls back to title order when no
      // year can be parsed from either field. Catalog payload only carries
      // `title`, `edition`, and `chapterCount`, so we extract the year from
      // whichever string surfaces it first.
      const sortedBooks = [...(result.books ?? [])].sort((a, b) => {
        const yearOf = (b: typeof a) => {
          const m = (b.edition || '').match(/\b(19|20)\d{2}\b/) ?? (b.title || '').match(/\b(19|20)\d{2}\b/);
          return m ? Number(m[0]) : 0;
        };
        const ay = yearOf(a);
        const by = yearOf(b);
        if (ay !== by) return by - ay;
        return (b.title || '').localeCompare(a.title || '');
      });
      setCatalog(sortedBooks);
      if (result.notice) setCatalogNotice(result.notice);
    } catch (e) {
      setCatalogNotice((e as Error).message);
    } finally {
      setCatalogLoading(false);
    }
  }

  async function handleDownload(book: CatalogBook) {
    const bookKey = getVendorBookKey(book);
    setDownloadingBookKey(bookKey);
    try {
      const { jobId } = await codeSetVendorService.triggerDownload(tenantId, {
        vendorId: selectedVendorId,
        bookId: book.bookId,
        edition: book.edition,
        title: book.title,
      });
      setRf(prev => {
        const vendors = (prev.codeSetVendors ?? []).map(v => {
          if (v.vendorId !== selectedVendorId) return v;
          const books = v.selectedBooks ?? [];
          const idx = books.findIndex(b => isSameVendorBook(b, book));
          const existing = idx >= 0 ? books[idx] : undefined;
          const newBook: VendorBook = {
            ...existing,
            bookId: book.bookId,
            title: book.title,
            edition: book.edition,
            downloadStatus: 'pending',
            downloadJobId: jobId,
            downloadPhase: 'queued',
            progressPercent: 0,
            processedRecords: 0,
            hydrationOperation: 'hydrate',
          };
          if (idx >= 0) { books[idx] = newBook; } else { books.push(newBook); }
          return { ...v, selectedBooks: [...books] };
        });
        return { ...prev, codeSetVendors: vendors };
      });
    } finally {
      setDownloadingBookKey(null);
    }
  }

  async function handleRehydrate(book: VendorBook | CatalogBook) {
    const bookKey = getVendorBookKey(book);
    const existingBook = getBookStatus(book);
    const title = existingBook?.title ?? book.title;
    const edition = existingBook?.edition ?? book.edition;
    setRehydratingBookKey(bookKey);
    try {
      const { jobId } = await codeSetVendorService.rehydrateTenantBook(tenantId, {
        vendorId: selectedVendorId,
        bookId: book.bookId,
        edition,
        title,
      });
      setRf(prev => {
        const vendors = (prev.codeSetVendors ?? []).map(v => {
          if (v.vendorId !== selectedVendorId) return v;
          const books = v.selectedBooks ?? [];
          const idx = books.findIndex(b => isSameVendorBook(b, book));
          const existing = idx >= 0 ? books[idx] : undefined;
          const newBook: VendorBook = {
            ...existing,
            bookId: book.bookId,
            title,
            edition,
            downloadStatus: 'pending',
            downloadJobId: jobId,
            downloadPhase: 'queued',
            progressPercent: 0,
            processedRecords: 0,
            hydrationOperation: 'rehydrate',
          };
          if (idx >= 0) { books[idx] = newBook; } else { books.push(newBook); }
          return { ...v, selectedBooks: [...books] };
        });
        return { ...prev, codeSetVendors: vendors };
      });
    } finally {
      setRehydratingBookKey(null);
    }
  }

  async function handleRemoveBook() {
    const book = removeBookTarget;
    if (!book) return;
    const bookKey = getVendorBookKey(book);
    setRemovingBookKey(bookKey);
    setCatalogNotice(null);
    try {
      const next = await codeSetVendorService.removeTenantBook(tenantId, {
        vendorId: selectedVendorId,
        bookId: book.bookId,
        edition: book.edition,
      });
      setRf(next);
      setExpandedBookKey(current => current === bookKey ? null : current);
      setRemoveBookTarget(null);
    } catch (e) {
      setCatalogNotice((e as Error).message);
    } finally {
      setRemovingBookKey(null);
    }
  }

  function handleBookComplete(vendorId: string, updatedBook: VendorBook) {
    setRf(prev => ({
      ...prev,
      codeSetVendors: (prev.codeSetVendors ?? []).map(v =>
        v.vendorId !== vendorId ? v : {
          ...v,
          selectedBooks: (v.selectedBooks ?? []).map(b => isSameVendorBook(b, updatedBook) ? updatedBook : b),
        }
      ),
    }));
  }

  const vendors = rf.codeSetVendors ?? [];
  const selectedVendorData = vendors.find(v => v.vendorId === selectedVendorId);
  const selectedBooks = selectedVendorData?.selectedBooks ?? [];
  const sortedCatalogBooks = [...catalog].sort((left, right) => {
    const yearDifference = resolveCatalogEditionYear(right.edition) - resolveCatalogEditionYear(left.edition);
    if (yearDifference !== 0) return yearDifference;
    const editionDifference = String(right.edition ?? '').localeCompare(String(left.edition ?? ''));
    if (editionDifference !== 0) return editionDifference;
    return left.title.localeCompare(right.title);
  });

  function getBookStatus(book: Pick<VendorBook | CatalogBook, 'bookId' | 'edition'>): VendorBook | undefined {
    return selectedVendorData?.selectedBooks?.find(b => isSameVendorBook(b, book));
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: theme.accent }} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">

      {/* Vendor Credentials */}
      <div className="rounded-lg border p-4 space-y-3" style={{ borderColor: theme.cardBorder, background: theme.expandedBg }}>
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: theme.textSecondary }}>Vendor Credentials</h4>
          <button onClick={() => { setEditingVendor(undefined); setShowAddVendor(true); }}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg transition-colors"
            style={{ background: theme.accent + '20', color: theme.accent }}>
            <Plus className="h-3 w-3" /> Add Vendor
          </button>
        </div>

        {vendors.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Link2 className="h-8 w-8 mb-3" style={{ color: theme.textSecondary + '80' }} />
            <p className="text-sm font-medium mb-1" style={{ color: theme.textPrimary }}>No vendors configured</p>
            <p className="text-xs mb-3" style={{ color: theme.textSecondary }}>Add an ICC or NFPA vendor to start importing code sets to this department.</p>
            <button onClick={() => { setEditingVendor(undefined); setShowAddVendor(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors"
              style={{ background: theme.accent + '20', color: theme.accent }}>
              <Plus className="h-3.5 w-3.5" /> Add Vendor
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {vendors.map(v => (
              <div key={v.vendorId} className="rounded-lg border p-3" style={{ borderColor: theme.cardBorder, background: theme.cardBg }}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium" style={{ color: theme.textPrimary }}>{v.vendorName}</span>
                      <ValidationBadge status={v.lastValidationStatus} />
                    </div>
                    <div className="mt-1 text-xs space-y-0.5" style={{ color: theme.textSecondary }}>
                      {v.credentials.clientId && <p>Client ID: {v.credentials.clientId}</p>}
                      {v.credentials.apiKey && <p>API Key: {v.credentials.apiKey}</p>}
                      {v.credentials.subscriptionId && <p>Subscription: {v.credentials.subscriptionId}</p>}
                      {v.lastValidatedAt && <p>Validated: {new Date(v.lastValidatedAt).toLocaleDateString()}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => handleValidateVendor(v.vendorId)} disabled={validatingVendorId === v.vendorId}
                      className="px-2 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors flex items-center gap-1">
                      {validatingVendorId === v.vendorId ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                      Validate
                    </button>
                    <button onClick={() => { setEditingVendor(v); setShowAddVendor(true); }}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors">
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Department Code Set Hydration */}
      <div className="rounded-lg border p-4 space-y-3" style={{ borderColor: theme.cardBorder, background: theme.expandedBg }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: theme.textSecondary }}>Department Hydrated Code Sets</h4>
            <p className="mt-1 text-xs" style={{ color: theme.textSecondary }}>
              Manage books assigned to this department and rehydrate tenant data from the system cache.
            </p>
          </div>
          <span className="text-xs font-medium" style={{ color: theme.textSecondary }}>
            {selectedBooks.length} {selectedBooks.length === 1 ? 'book' : 'books'}
          </span>
        </div>

        {vendors.length === 0 ? (
          <p className="text-xs py-2" style={{ color: theme.textSecondary }}>Add and validate a vendor above before assigning books to this department.</p>
        ) : (
          <>
            {selectedBooks.length > 0 && (
              <div className="overflow-hidden rounded-lg border" style={{ borderColor: theme.cardBorder }}>
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ background: theme.expandedBg }}>
                      <th className="text-left px-3 py-2 font-medium" style={{ color: theme.textSecondary }}>Book</th>
                      <th className="text-center px-2 py-2 font-medium w-20" style={{ color: theme.textSecondary }}>Edition</th>
                      <th className="text-center px-2 py-2 font-medium w-24" style={{ color: theme.textSecondary }}>Chapters</th>
                      <th className="text-center px-2 py-2 font-medium w-28" style={{ color: theme.textSecondary }}>Violations</th>
                      <th className="text-right px-3 py-2 font-medium w-56" style={{ color: theme.textSecondary }}>Hydration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBooks.map((book, idx) => {
                      const bookKey = getVendorBookKey(book);
                      const isExpanded = expandedBookKey === bookKey;
                      const isRemoving = removingBookKey === bookKey;
                      const isHydrating = book.downloadStatus === 'pending' || book.downloadStatus === 'downloading';
                      return (
                        <React.Fragment key={bookKey}>
                          <tr className={`border-t ${idx % 2 === 0 ? '' : 'bg-black/[0.02]'}`} style={{ borderColor: theme.cardBorder }}>
                            <td className="px-3 py-2">
                              <button
                                onClick={() => book.downloadStatus === 'completed' && setExpandedBookKey(isExpanded ? null : bookKey)}
                                className={`flex items-center gap-1 text-left ${book.downloadStatus === 'completed' ? 'cursor-pointer hover:underline' : 'cursor-default'}`}
                                style={{ color: theme.textPrimary }}
                              >
                                {book.downloadStatus === 'completed' ? (
                                  isExpanded ? <ChevronDown className="h-3 w-3 flex-shrink-0" /> : <ChevronRight className="h-3 w-3 flex-shrink-0" />
                                ) : <span className="w-3" />}
                                {book.title}
                              </button>
                            </td>
                            <td className="text-center px-2 py-2" style={{ color: theme.textSecondary }}>{book.edition}</td>
                            <td className="text-center px-2 py-2" style={{ color: theme.textSecondary }}>{book.chapterCount ?? '—'}</td>
                            <td className="text-center px-2 py-2" style={{ color: theme.textSecondary }}>{book.violationCount?.toLocaleString() ?? '—'}</td>
                            <td className="text-right px-3 py-2">
                              {isHydrating && book.downloadJobId ? (
                                <DownloadProgressRow
                                  tenantId={tenantId}
                                  book={book}
                                  catalogBook={book}
                                  onComplete={updated => handleBookComplete(selectedVendorId, updated)}
                                />
                              ) : book.downloadStatus === 'failed' ? (
                                <div className="flex flex-col items-end gap-1">
                                  <div className="flex items-center justify-end gap-2">
                                    <span className="text-xs text-red-600">Failed</span>
                                    <button onClick={() => handleRehydrate(book)} disabled={isRemoving}
                                      className="text-xs text-orange-600 hover:text-orange-700 underline disabled:opacity-50">Retry Rehydrate</button>
                                    <button onClick={() => setRemoveBookTarget(book)} disabled={isRemoving}
                                      className="text-xs text-red-600 hover:text-red-700 underline disabled:opacity-50">
                                      {isRemoving ? 'Removing…' : 'Remove'}
                                    </button>
                                  </div>
                                  {book.errorMessage && (
                                    <p className="max-w-[260px] text-right text-[11px] leading-snug text-red-700">
                                      {book.errorMessage}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center justify-end gap-2">
                                  <DownloadBadge status={book.downloadStatus} violations={book.violationCount} importedAt={book.downloadedAt} />
                                  <button onClick={() => handleRehydrate(book)} disabled={rehydratingBookKey === bookKey || isRemoving}
                                    className="flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-orange-300 text-orange-600 hover:bg-orange-50 disabled:opacity-50 transition-colors">
                                    {rehydratingBookKey === bookKey ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                                    Rehydrate
                                  </button>
                                  <button onClick={() => setRemoveBookTarget(book)} disabled={isRemoving}
                                    className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors disabled:opacity-50"
                                    title="Remove this department assignment">
                                    {isRemoving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>

                          {isExpanded && book.downloadStatus === 'completed' && (
                            <tr className="border-t" style={{ borderColor: theme.cardBorder }}>
                              <td colSpan={5} className="px-4 py-3" style={{ background: theme.expandedBg }}>
                                <div className="text-xs space-y-1" style={{ color: theme.textSecondary }}>
                                  <p className="font-medium mb-2" style={{ color: theme.textPrimary }}>
                                    {book.chapterCount ?? '?'} chapters · {book.violationCount?.toLocaleString() ?? '?'} violations
                                    {book.downloadedAt && ` · Hydrated on ${new Date(book.downloadedAt).toLocaleDateString()}`}
                                  </p>
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                    <div className="rounded-md border px-2 py-1.5" style={{ borderColor: theme.cardBorder, background: theme.cardBg }}>
                                      <p className="text-[10px] uppercase tracking-wide" style={{ color: theme.textSecondary }}>Last operation</p>
                                      <p className="font-medium" style={{ color: theme.textPrimary }}>{book.hydrationOperation === 'rehydrate' ? 'Tenant rehydration' : 'Initial hydrate'}</p>
                                    </div>
                                    <div className="rounded-md border px-2 py-1.5 min-w-0" style={{ borderColor: theme.cardBorder, background: theme.cardBg }}>
                                      <p className="text-[10px] uppercase tracking-wide" style={{ color: theme.textSecondary }}>Raw artifact</p>
                                      <p className="font-mono truncate" style={{ color: theme.textPrimary }}>{book.rawArtifactId ?? '—'}</p>
                                    </div>
                                    <div className="rounded-md border px-2 py-1.5 min-w-0" style={{ borderColor: theme.cardBorder, background: theme.cardBg }}>
                                      <p className="text-[10px] uppercase tracking-wide" style={{ color: theme.textSecondary }}>Tenant code set</p>
                                      <p className="font-mono truncate" style={{ color: theme.textPrimary }}>{book.codeSetId ?? '—'}</p>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {selectedBooks.length === 0 && (
              <p className="text-xs py-2" style={{ color: theme.textSecondary }}>No books are currently assigned to this department. Load a vendor catalog to assign the first book.</p>
            )}

            <div className="flex items-center gap-2">
              <select value={selectedVendorId} onChange={e => { setSelectedVendorId(e.target.value); setCatalog([]); setCatalogNotice(null); }}
                className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-orange-500">
                {vendors.map(v => <option key={v.vendorId} value={v.vendorId}>{v.vendorName}</option>)}
              </select>
              <button onClick={handleLoadCatalog} disabled={catalogLoading || !selectedVendorId}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
                {catalogLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BookOpen className="h-3.5 w-3.5" />}
                Load Catalog
              </button>
              <span className="text-xs" style={{ color: theme.textSecondary }}>
                Browse the full vendor catalog, including books already hydrated to this department.
              </span>
            </div>

            {catalogNotice && (
              <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">{catalogNotice}</p>
              </div>
            )}

            {sortedCatalogBooks.length > 0 && (
              <div className="overflow-hidden rounded-lg border" style={{ borderColor: theme.cardBorder }}>
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ background: theme.expandedBg }}>
                      <th className="text-left px-3 py-2 font-medium" style={{ color: theme.textSecondary }}>Title</th>
                      <th className="text-center px-2 py-2 font-medium w-16" style={{ color: theme.textSecondary }}>Ed.</th>
                      <th className="text-center px-2 py-2 font-medium w-14" style={{ color: theme.textSecondary }}>Ch.</th>
                      <th className="text-right px-3 py-2 font-medium w-40" style={{ color: theme.textSecondary }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCatalogBooks.map((book, idx) => {
                      const bookStatus = getBookStatus(book);
                      const bookKey = getVendorBookKey(book);
                      const isHydrating = bookStatus?.downloadStatus === 'pending' || bookStatus?.downloadStatus === 'downloading';
                      const isAssigning = downloadingBookKey === bookKey;
                      const isRehydrating = rehydratingBookKey === bookKey;
                      const isRemoving = removingBookKey === bookKey;

                      return (
                        <React.Fragment key={bookKey}>
                          <tr className={`border-t ${idx % 2 === 0 ? '' : 'bg-black/[0.02]'}`} style={{ borderColor: theme.cardBorder }}>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1 text-left" style={{ color: theme.textPrimary }}>
                                <span className="w-3" />
                                {book.title}
                              </div>
                            </td>
                            <td className="text-center px-2 py-2" style={{ color: theme.textSecondary }}>{book.edition}</td>
                            <td className="text-center px-2 py-2" style={{ color: theme.textSecondary }}>{book.chapterCount ?? '—'}</td>
                            <td className="text-right px-3 py-2">
                              {isHydrating && bookStatus?.downloadJobId ? (
                                <DownloadProgressRow
                                  tenantId={tenantId}
                                  book={bookStatus}
                                  catalogBook={book}
                                  onComplete={updated => handleBookComplete(selectedVendorId, updated)}
                                />
                              ) : bookStatus?.downloadStatus === 'completed' ? (
                                <div className="flex items-center justify-end gap-2">
                                  <DownloadBadge status="completed" violations={bookStatus.violationCount} importedAt={bookStatus.downloadedAt} />
                                  <button onClick={() => handleRehydrate(bookStatus)} disabled={isRehydrating}
                                    className="flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-orange-300 text-orange-600 hover:bg-orange-50 disabled:opacity-50 transition-colors">
                                    {isRehydrating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                                    Rehydrate
                                  </button>
                                </div>
                              ) : bookStatus?.downloadStatus === 'failed' ? (
                                <div className="flex flex-col items-end gap-1">
                                  <div className="flex items-center justify-end gap-2">
                                    <span className="text-xs text-red-600">Failed</span>
                                    <button onClick={() => handleRehydrate(bookStatus)} disabled={isRemoving}
                                      className="text-xs text-orange-600 hover:text-orange-700 underline disabled:opacity-50">Retry Rehydrate</button>
                                    <button onClick={() => setRemoveBookTarget(bookStatus)} disabled={isRemoving}
                                      className="text-xs text-red-600 hover:text-red-700 underline disabled:opacity-50">
                                      {isRemoving ? 'Removing…' : 'Remove'}
                                    </button>
                                  </div>
                                  {bookStatus.errorMessage && (
                                    <p className="max-w-[260px] text-right text-[11px] leading-snug text-red-700">
                                      {bookStatus.errorMessage}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <button onClick={() => handleDownload(book)} disabled={isAssigning}
                                  className="flex items-center gap-1 ml-auto px-2 py-1 text-xs rounded-md border border-orange-300 text-orange-600 hover:bg-orange-50 disabled:opacity-50 transition-colors">
                                  {isAssigning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                                  Assign & Hydrate
                                </button>
                              )}
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {catalog.length > 0 && sortedCatalogBooks.length === 0 && (
              <p className="text-xs py-2" style={{ color: theme.textSecondary }}>No catalog books were returned for this vendor.</p>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showAddVendor && (
        <AddVendorModal
          existing={editingVendor}
          tenantId={tenantId}
          theme={theme}
          onClose={() => { setShowAddVendor(false); setEditingVendor(undefined); }}
          onSaved={saved => {
            setShowAddVendor(false);
            setEditingVendor(undefined);
            loadRegulatoryFramework();
          }}
        />
      )}

      {removeBookTarget && (
        <ConfirmActionModal
          title="Remove Code Set Assignment?"
          description={<>Remove <strong>{removeBookTarget.title}</strong> ({removeBookTarget.edition}) from this department's hydrated code sets?</>}
          warning="This only removes the department assignment. It does not delete the system cache or hydrated tenant code-set data."
          confirmButtonLabel="Remove Assignment"
          variant="danger"
          isSubmitting={removingBookKey === getVendorBookKey(removeBookTarget)}
          onConfirm={handleRemoveBook}
          onClose={() => {
            if (!removingBookKey) setRemoveBookTarget(null);
          }}
        />
      )}
    </div>
  );
}
